import { Level, LevelType } from '../types/Level';
import { DraftLevel } from './types';
import { WORD_SEARCH_WORDS } from './rules';

export const nextFreeId = (levels: { id: string }[]): string => {
  const numbers = levels.map(level => parseInt(level.id, 10)).filter(number => !isNaN(number));
  return numbers.length === 0 ? '1' : (Math.max(...numbers) + 1).toString();
};

// Why a word search cannot be approved as it is, or null when it is fine. Other types always return null.
export const wordSearchProblem = (draft: DraftLevel): string | null => {
  if (draft.levelType !== LevelType.WordSearch || !draft.wordSearchGrid) return null;
  const placed = draft.wordSearchGrid.wordPositions.length;
  return placed === WORD_SEARCH_WORDS
    ? null
    : `This word search has ${placed} word${placed === 1 ? '' : 's'}; it needs exactly ${WORD_SEARCH_WORDS}. Fix it in Edit, or reject it.`;
};

export const canApprove = (draft: DraftLevel): boolean => draft.status === 'ready' && wordSearchProblem(draft) === null;

// "Approve all" takes only levels with no warnings; a flagged level needs a look and its own Approve click.
export const canBulkApprove = (draft: DraftLevel): boolean => canApprove(draft) && draft.flags.length === 0;

// Only the fields the game uses for this level type; never an undefined value.
export const draftToLevel = (draft: DraftLevel): Omit<Level, 'id'> => {
  const level: Omit<Level, 'id'> = { levelType: draft.levelType, difficulty: draft.difficulty };
  if (draft.funFact) level.funFact = draft.funFact;

  const addKeyLetters = () => {
    if (draft.keyLetters && draft.keyLetters.length > 0) level.keyLetters = draft.keyLetters;
  };
  const addImage = () => {
    if (draft.image) {
      level.image = draft.image;
      if (draft.imageSize) level.imageSize = draft.imageSize;
    }
  };

  switch (draft.levelType) {
    case LevelType.CategoryWords:
      if (draft.category) level.category = draft.category;
      if (draft.words && draft.words.length > 0) level.words = draft.words;
      addKeyLetters();
      break;

    case LevelType.WordSearch:
      if (draft.category) level.category = draft.category;
      if (draft.wordSearchGrid) {
        level.wordSearchGrid = draft.wordSearchGrid;
      } else if (draft.words && draft.words.length > 0) {
        // Only a level without a grid keeps the plain word list, as a starting point for the grid editor
        level.words = draft.words;
      }
      break;

    case LevelType.WhereIsThisLocated:
      if (draft.sentence) level.sentence = draft.sentence;
      if (draft.locationName) level.locationName = draft.locationName;
      addKeyLetters();
      addImage();
      break;

    default:
      if (draft.sentence) level.sentence = draft.sentence;
      addKeyLetters();
      addImage();
      break;
  }

  return level;
};

// What the level form is opened with when the user clicks Edit on a draft.
export const draftToPrefill = (draft: DraftLevel, id: string): Partial<Level> => {
  const prefill: Partial<Level> = { id, ...draftToLevel(draft) };
  // The grid editor receives the words as suggestions; saving them here would store words that were never placed
  if (draft.levelType === LevelType.WordSearch) delete prefill.words;
  return prefill;
};

// Thrown by the level writer when a level with that number already exists.
export class LevelExistsError extends Error {
  constructor(id: string) {
    super(`Level ${id} already exists.`);
    this.name = 'LevelExistsError';
    Object.setPrototypeOf(this, LevelExistsError.prototype);
  }
}

export interface PublishDeps {
  // Must read the real level list and throw when it cannot (never an empty or cached stand-in)
  loadLevels: () => Promise<Level[]>;
  // Must refuse to overwrite: throws LevelExistsError when the number is taken
  createLevel: (id: string, data: Omit<Level, 'id'>) => Promise<Level>;
}

const MAX_NUMBER_ATTEMPTS = 3;

export const publishDraft = async (draft: DraftLevel, deps: PublishDeps): Promise<Level> => {
  if (!canApprove(draft)) {
    throw new Error('This level is not ready to approve.');
  }

  for (let attempt = 0; attempt < MAX_NUMBER_ATTEMPTS; attempt++) {
    // Reload each time so the number reflects levels created a moment ago
    const levels = await deps.loadLevels();
    try {
      return await deps.createLevel(nextFreeId(levels), draftToLevel(draft));
    } catch (err) {
      if (!(err instanceof LevelExistsError)) throw err;
    }
  }
  throw new Error('Could not find a free level number. Try again.');
};
